
import { IPlanificationProfileScheduler } from '@/entity/planification/planification-profile-scheduler';
import { NU } from '@t/type';
import { addBusinessDays, addDays, differenceInBusinessDays, format, isValid, max, min } from 'date-fns';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { dateToString, getUserByIds, IPhaseProfileByKey, isEndDateAvailable, isStartDateAvailable, MgtPersonDic, removeHour } from '../calendar-helper';
import { CalculatedDate, ConfigShifted, newShiftedWorkDate } from './scheduler-shifting-helper';

@Component({})
export default class SchedulerShifting extends Vue {
    /* item sélectionné par l'utilisateur */
    @Prop({ required: true }) shiftedItems!: IPhaseProfileByKey[];
    @Prop({ required: true }) phases!: IPlanificationProfileScheduler[];
    version: number = 0;
    mgtUsers: MgtPersonDic = {};
    configShifted: ConfigShifted[] = [];
    fromStartDate: string | null = null;
    shiftDate: string | null = null;
    isDebug: boolean = false;

    get shiftStats() {
        const count = this.configShifted
            .filter(cs => cs.phase.parentPhaseId > 0)
            .reduce<number[]>((acc, item) => {
                if (!acc.includes(item.phase.parentPhaseId)) {
                    acc.push(item.phase.parentPhaseId);
                }
                return acc;
            }, [])
            .length;
        
        return {
            phase: `${count} phase${count > 1 ? 's' : ''}`,
            profil: `${this.configShifted.length} profil${this.configShifted.length > 1 ? 's' : ''}`
        };
    }

    isStartDateDisabled(_, date: Date) {
        return isStartDateAvailable(_, date, this.shiftDate);
    }

    isEndDateDisabled(_, date: Date) {
        return isEndDateAvailable(_, date, this.fromStartDate);
    }

    mounted() {
        this.onWatchShiftedItems();
    }

    shiftConfirm() {
        if (this.configShifted.length > 0) {
            this.$emit('confirmSave');
        }
    }

    dateToString(date: NU<Date | string>, currentFormat: string, numberDaysAdd?: number): string {
        return dateToString(date, currentFormat, numberDaysAdd);
    }

    @Watch('fromStartDate')
    setAfterDate(newVal: string) {
        if (!this.shiftDate) {
            this.shiftDate = format(addDays(new Date(newVal), 1), 'yyyy-MM-dd');
        }
    }

    @Watch('shiftedItems', { deep: true })
    @Watch('shiftDate')
    @Watch('fromStartDate')
    onWatchShiftedItems() {
        getUserByIds(
            this.phases.flatMap((m) => m.employeeId).filter((m) => m && m.length > 0),
            this.mgtUsers,
            false
        );
        this.configShifted = this.shiftedItems
            .map((s) => {
                const phase = this.phases.find(
                    (p) => p.contractLineId === s.value.contractLineId && p.indexInPhase === s.value.indexInPhase
                );
                return { key: s.key, phase: phase!, shifts: this.shiftCalculation(phase!) };
            })
            .filter((s) => s !== null);
        const defaultDate = min(
            this.configShifted
                .flatMap((fm) => fm.phase.startDate)
                .filter((f) => f)
                .map((f) => f as Date)
        );
        this.fromStartDate =
            !this.fromStartDate && isValid(defaultDate) ? format(defaultDate, 'yyyy-MM-dd') : this.fromStartDate;
        this.shiftDate = !this.shiftDate && isValid(defaultDate) ? format(defaultDate, 'yyyy-MM-dd') : this.shiftDate;
        this.$emit('dateToShift', [...this.configShifted]);
    }

    shiftCalculation(phase: IPlanificationProfileScheduler): CalculatedDate {
        const defaultObj: CalculatedDate = { shiftCount: 0, workoutDates: [], min: '', max: '' };
        if (
            phase &&
            this.shiftDate &&
            this.fromStartDate &&
            isValid(new Date(this.shiftDate)) &&
            isValid(new Date(this.fromStartDate))
        ) {
            const endDate: Date = removeHour(new Date(this.dateToString(phase.endDate, 'yyyy-MM-dd', 0)));
            const fromStartDate: Date = removeHour(new Date(this.dateToString(this.fromStartDate, 'yyyy-MM-dd', 0)));
            const shiftedDate: Date = removeHour(new Date(this.dateToString(this.shiftDate, 'yyyy-MM-dd', 0)));

            if (shiftedDate < fromStartDate || fromStartDate > endDate) {
                return defaultObj;
            }
            defaultObj.shiftCount = differenceInBusinessDays(shiftedDate, fromStartDate);
            if (Array.isArray(phase.planificationPhaseProfileWorkDays)) {
                phase.planificationPhaseProfileWorkDays
                    .filter((w) => {
                        return (
                            w.date &&
                            new Date(this.dateToString(w.date, 'yyyy-MM-dd', 0)) >= fromStartDate &&
                            defaultObj.shiftCount > 0
                        );
                    })
                    .sort((a, b) => (a.date < b.date ? -1 : 1))
                    .forEach((w) => {
                        const shiftDate = addBusinessDays(w.date, defaultObj.shiftCount);
                        defaultObj.workoutDates.push(newShiftedWorkDate(w.date, shiftDate));
                    });
                const minVal = min(defaultObj.workoutDates.map((wd) => wd.shiftDate.date));
                const maxVal = max(defaultObj.workoutDates.map((wd) => wd.shiftDate.date));
                defaultObj.min = isValid(minVal) ? this.dateToString(minVal, 'dd/MM/yyyy') : '';
                defaultObj.max = isValid(maxVal) ? this.dateToString(maxVal, 'dd/MM/yyyy') : '';
            }
        }
        return defaultObj;
    }
}
